import { POINT_BYTES, REFERENCE_FONT_SIZE, REFERENCE_BLOCK_WIDTH, MIN_SEGMENT_POINTS, STROKE_FORMAT_VERSION, STROKE_TOOLS, ANCHOR_KINDS, STROKE_HINTS, ORPHAN_STROKE_PURGE_DAYS } from './chunk-SSS7IHSV.js';
import { pmContentToY, yFragmentToPM } from './chunk-TCGBD5LJ.js';
import { NOTE_ROOTS } from './chunk-PP2CXEGZ.js';
import { uuidv7 } from './chunk-RPUENDSP.js';
import * as Y from 'yjs';

// src/strokes/codec.ts
function packPoints(points) {
  const out = new Uint8Array(points.length * POINT_BYTES);
  const view = new DataView(out.buffer);
  points.forEach((p, i) => {
    const o = i * POINT_BYTES;
    view.setFloat32(o, p.a, true);
    view.setFloat32(o + 4, p.x, true);
    view.setFloat32(o + 8, p.y, true);
    view.setFloat32(o + 12, p.pressure, true);
    view.setFloat32(o + 16, p.altitude, true);
    view.setFloat32(o + 20, p.azimuth, true);
    view.setFloat32(o + 24, p.roll, true);
    view.setFloat32(o + 28, p.t, true);
  });
  return out;
}
function pointCount(packed) {
  if (packed.length % POINT_BYTES !== 0) {
    throw new RangeError(
      `packed points length ${packed.length} is not a multiple of ${POINT_BYTES}`
    );
  }
  return packed.length / POINT_BYTES;
}
function unpackPoints(packed) {
  const n = pointCount(packed);
  const view = new DataView(packed.buffer, packed.byteOffset, packed.byteLength);
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * POINT_BYTES;
    out[i] = {
      a: view.getFloat32(o, true),
      x: view.getFloat32(o + 4, true),
      y: view.getFloat32(o + 8, true),
      pressure: view.getFloat32(o + 12, true),
      altitude: view.getFloat32(o + 16, true),
      azimuth: view.getFloat32(o + 20, true),
      roll: view.getFloat32(o + 24, true),
      t: view.getFloat32(o + 28, true)
    };
  }
  return out;
}
function pointsAsFloat32(packed) {
  pointCount(packed);
  if (packed.byteOffset % 4 === 0) {
    return new Float32Array(packed.buffer, packed.byteOffset, packed.byteLength / 4);
  }
  return new Float32Array(packed.slice().buffer);
}
var DEFAULT_POINT = Object.freeze({
  pressure: 1,
  altitude: Math.PI / 2,
  azimuth: 0,
  roll: 0
});
function encodeAnchor(text, index) {
  return Y.encodeRelativePosition(Y.createRelativePositionFromTypeIndex(text, index, 0));
}
function isCharDeleted(store, id) {
  let client = id.client;
  let clock = id.clock;
  for (let guard = 0; guard < 1e3; guard++) {
    if (Y.getState(store, client) <= clock) return true;
    const struct = Y.getItem(store, Y.createID(client, clock));
    if (!(struct instanceof Y.Item)) return true;
    if (struct.redone !== null) {
      const diff = clock - struct.id.clock;
      client = struct.redone.client;
      clock = struct.redone.clock + diff;
      continue;
    }
    if (struct.deleted) return true;
    let parent = struct.parent?._item ?? null;
    while (parent) {
      if (parent.deleted) return true;
      parent = parent.parent?._item ?? null;
    }
    return false;
  }
  return true;
}
function resolveAnchor(doc, encoded) {
  let rpos;
  try {
    rpos = Y.decodeRelativePosition(encoded);
  } catch {
    return null;
  }
  const abs = Y.createAbsolutePositionFromRelativePosition(rpos, doc);
  if (!abs || !(abs.type instanceof Y.XmlText)) return null;
  const deleted = rpos.item ? isCharDeleted(doc.store, rpos.item) : true;
  return { text: abs.type, index: abs.index, deleted };
}
function resolveAnchors(doc, anchors) {
  return anchors.map((a) => resolveAnchor(doc, a));
}
function allAnchorsDeleted(resolved) {
  return resolved.every((r) => r === null || r.deleted);
}
function rebaseAnchorList(doc, anchors) {
  let changed = false;
  const out = anchors.map((encoded) => {
    let rpos;
    try {
      rpos = Y.decodeRelativePosition(encoded);
    } catch {
      return encoded;
    }
    if (!rpos.item || Y.getState(doc.store, rpos.item.client) <= rpos.item.clock) return encoded;
    const original = Y.getItem(doc.store, rpos.item);
    if (!(original instanceof Y.Item) || original.redone === null) return encoded;
    const resolved = resolveAnchor(doc, encoded);
    if (!resolved || resolved.deleted) return encoded;
    changed = true;
    return encodeAnchor(resolved.text, resolved.index);
  });
  return changed ? out : null;
}

// src/strokes/width.ts
function pressureCurve(pressure) {
  const p = Math.min(Math.max(pressure, 0), 1);
  return 0.35 + 0.65 * Math.pow(p, 0.8);
}
function tiltFactor(tool, altitude) {
  if (tool !== "pencil" && tool !== "marker") return 1;
  const a = Math.min(Math.max(altitude, 0), Math.PI / 2);
  return 1 + 1.5 * (1 - a / (Math.PI / 2));
}
function strokeWidthAt(stroke, point, scale) {
  const s = "fontSize" in scale ? scale.fontSize / REFERENCE_FONT_SIZE : scale.blockWidth / REFERENCE_BLOCK_WIDTH;
  return stroke.width * pressureCurve(point.pressure) * s * tiltFactor(stroke.tool, point.altitude);
}
function drawsUnderText(tool) {
  return tool === "highlighter";
}

// src/strokes/layout.ts
function runs(items, breakBetween) {
  const out = [];
  let cur = [];
  let prev = null;
  for (const item of items) {
    if (item === null) {
      if (cur.length) out.push(cur);
      cur = [];
      prev = null;
      continue;
    }
    if (prev !== null && breakBetween(prev, item)) {
      out.push(cur);
      cur = [];
    }
    cur.push(item);
    prev = item;
  }
  if (cur.length) out.push(cur);
  return out.filter((r) => r.length >= MIN_SEGMENT_POINTS);
}
function layoutTextStroke(stroke, points, anchors, glyph) {
  const boxes = /* @__PURE__ */ new Map();
  const boxOf = (a) => {
    if (!boxes.has(a)) {
      const anchor = anchors[a];
      boxes.set(a, anchor && !anchor.deleted ? glyph(anchor) : null);
    }
    return boxes.get(a);
  };
  const placed = points.map((p) => {
    const box = Number.isInteger(p.a) && p.a >= 0 ? boxOf(p.a) : null;
    if (!box) return null;
    return {
      line: box.line,
      point: {
        x: box.x + p.x * box.fontSize,
        y: box.baseline + p.y * box.fontSize,
        width: strokeWidthAt(stroke, p, { fontSize: box.fontSize }),
        pressure: p.pressure,
        altitude: p.altitude,
        azimuth: p.azimuth,
        roll: p.roll,
        t: p.t
      }
    };
  });
  return runs(placed, (a, b) => a.line !== b.line).map((r) => r.map((e) => e.point));
}
function layoutBlockStroke(stroke, points, box) {
  const pts = points.map((p) => ({
    x: box.x + p.x * box.width,
    y: box.y + p.y * box.width,
    width: strokeWidthAt(stroke, p, { blockWidth: box.width }),
    pressure: p.pressure,
    altitude: p.altitude,
    azimuth: p.azimuth,
    roll: p.roll,
    t: p.t
  }));
  return pts.length >= MIN_SEGMENT_POINTS ? [pts] : [];
}
function anchorTextStroke(input, hitTest) {
  const anchors = [];
  const indexOf = /* @__PURE__ */ new Map();
  const points = [];
  for (const p of input) {
    const hit = hitTest(p.x, p.y);
    if (!hit) continue;
    let byIndex = indexOf.get(hit.text);
    if (!byIndex) indexOf.set(hit.text, byIndex = /* @__PURE__ */ new Map());
    let a = byIndex.get(hit.index);
    if (a === void 0) {
      a = anchors.length;
      anchors.push(encodeAnchor(hit.text, hit.index));
      byIndex.set(hit.index, a);
    }
    points.push({
      a,
      x: (p.x - hit.box.x) / hit.box.fontSize,
      y: (p.y - hit.box.baseline) / hit.box.fontSize,
      pressure: p.pressure ?? 1,
      altitude: p.altitude ?? Math.PI / 2,
      azimuth: p.azimuth ?? 0,
      roll: p.roll ?? 0,
      t: p.t
    });
  }
  return points.length > 0 ? { anchors, points } : null;
}
function blockStrokePoints(input, box) {
  return input.map((p) => ({
    a: -1,
    x: (p.x - box.x) / box.width,
    y: (p.y - box.y) / box.width,
    pressure: p.pressure ?? 1,
    altitude: p.altitude ?? Math.PI / 2,
    azimuth: p.azimuth ?? 0,
    roll: p.roll ?? 0,
    t: p.t
  }));
}
function isTextAnchored(kind) {
  return kind === "text";
}
function strokesMap(doc) {
  return doc.getMap(NOTE_ROOTS.strokes);
}
function validateStroke(s) {
  const fail = (m) => {
    throw new TypeError(`invalid stroke: ${m}`);
  };
  if (s.v !== STROKE_FORMAT_VERSION) fail(`v must be ${STROKE_FORMAT_VERSION}`);
  if (!STROKE_TOOLS.includes(s.tool)) fail(`tool ${s.tool}`);
  if (!ANCHOR_KINDS.includes(s.anchorKind))
    fail(`anchorKind ${s.anchorKind}`);
  if (!STROKE_HINTS.includes(s.hint)) fail(`hint ${s.hint}`);
  if (!(s.width > 0)) fail("width must be > 0");
  if (!(s.opacity >= 0 && s.opacity <= 1)) fail("opacity must be 0..1");
  if (!(s.points instanceof Uint8Array)) fail("points must be bytes");
  pointCount(s.points);
  if (s.anchorKind === "text") {
    if (s.blockId !== null) fail("text strokes have no blockId");
    if (s.anchors.length === 0) fail("text strokes need anchors");
  } else {
    if (!s.blockId) fail(`${s.anchorKind} strokes need a blockId`);
    if (s.anchors.length !== 0) fail("only text strokes have anchors");
  }
}
function putStroke(doc, strokeId, stroke) {
  validateStroke(stroke);
  const m = new Y.Map();
  m.set("v", stroke.v);
  m.set("tool", stroke.tool);
  m.set("color", stroke.color);
  m.set("width", stroke.width);
  m.set("opacity", stroke.opacity);
  m.set("anchorKind", stroke.anchorKind);
  m.set("blockId", stroke.blockId);
  m.set(
    "anchors",
    stroke.anchors.map((a) => a.slice())
  );
  m.set("points", stroke.points.slice());
  m.set("hint", stroke.hint);
  m.set("author", { kind: stroke.author.kind, id: stroke.author.id });
  m.set("createdAt", stroke.createdAt);
  if (stroke.hidden !== void 0) m.set("hidden", stroke.hidden);
  strokesMap(doc).set(strokeId, m);
}
function bytes(v) {
  return v instanceof Uint8Array ? v : new Uint8Array(0);
}
function readStroke(map) {
  const v = map.get("v");
  if (v !== STROKE_FORMAT_VERSION) return null;
  const author = map.get("author") ?? {};
  const stroke = {
    v,
    tool: map.get("tool"),
    color: String(map.get("color") ?? "ink.black"),
    width: Number(map.get("width") ?? 1),
    opacity: Number(map.get("opacity") ?? 1),
    anchorKind: map.get("anchorKind"),
    blockId: map.get("blockId") ?? null,
    anchors: (map.get("anchors") ?? []).map(bytes),
    points: bytes(map.get("points")),
    hint: map.get("hint") ?? "free",
    author: { kind: author.kind, id: author.id },
    createdAt: Number(map.get("createdAt") ?? 0)
  };
  const hidden = map.get("hidden");
  if (typeof hidden === "boolean") stroke.hidden = hidden;
  return stroke;
}
function getStroke(doc, strokeId) {
  const m = strokesMap(doc).get(strokeId);
  return m instanceof Y.Map ? readStroke(m) : null;
}
function listStrokes(doc) {
  const out = [];
  strokesMap(doc).forEach((value, id) => {
    if (!(value instanceof Y.Map)) return;
    const stroke = readStroke(value);
    if (stroke) out.push({ id, stroke });
  });
  return out.sort((a, b) => a.stroke.createdAt - b.stroke.createdAt || (a.id < b.id ? -1 : 1));
}
function strokesForBlock(doc, blockId) {
  return listStrokes(doc).filter(
    (s) => s.stroke.anchorKind !== "text" && s.stroke.blockId === blockId
  );
}
function deleteStroke(doc, strokeId) {
  strokesMap(doc).delete(strokeId);
}
function replaceStroke(doc, strokeId, pieces) {
  doc.transact(() => {
    deleteStroke(doc, strokeId);
    for (const p of pieces) putStroke(doc, p.id, p.stroke);
  });
}
function setStrokeHidden(doc, strokeId, hidden) {
  const m = strokesMap(doc).get(strokeId);
  if (m instanceof Y.Map) m.set("hidden", hidden);
}
function findOrphanedStrokes(doc) {
  return listStrokes(doc).filter(
    ({ stroke }) => stroke.anchorKind === "text" && allAnchorsDeleted(resolveAnchors(doc, stroke.anchors))
  ).map(({ id }) => id);
}
function findPurgeableStrokes(doc, now, orphanedAt) {
  const limit = ORPHAN_STROKE_PURGE_DAYS * 24 * 60 * 60 * 1e3;
  return findOrphanedStrokes(doc).filter((id) => {
    const since = orphanedAt?.(id) ?? getStroke(doc, id)?.createdAt ?? now;
    return now - since > limit;
  });
}
function purgeStrokes(doc, strokeIds) {
  doc.transact(() => {
    for (const id of strokeIds) deleteStroke(doc, id);
  });
}
var STROKE_REBASE_ORIGIN = /* @__PURE__ */ Symbol("datascratch.strokes.rebase");
function rebaseRedoneAnchors(doc) {
  const changed = [];
  doc.transact(() => {
    strokesMap(doc).forEach((value, id) => {
      if (!(value instanceof Y.Map) || value.get("anchorKind") !== "text") return;
      const stroke = readStroke(value);
      if (!stroke) return;
      const next = rebaseAnchorList(doc, stroke.anchors);
      if (next) {
        value.set("anchors", next);
        changed.push(id);
      }
    });
  }, STROKE_REBASE_ORIGIN);
  return changed;
}
function attachAnchorRebase(undoManager) {
  const doc = undoManager.doc;
  const handler = () => {
    rebaseRedoneAnchors(doc);
  };
  undoManager.on("stack-item-popped", handler);
  return () => undoManager.off("stack-item-popped", handler);
}
function pathOf(text, root) {
  const path = [];
  let node = text;
  while (node !== root) {
    const parent = node.parent;
    if (!parent) return null;
    const i = parent.toArray().indexOf(node);
    if (i < 0) return null;
    path.unshift(i);
    node = parent;
  }
  return path;
}
function atPath(root, path) {
  let node = root;
  for (const i of path) {
    if (node instanceof Y.XmlText) return null;
    const next = node.get(i);
    if (!next) return null;
    node = next;
  }
  return node instanceof Y.XmlText ? node : null;
}
function rebaseAnchorsByIndex(oldDoc, newDoc, anchors) {
  const fromRoot = oldDoc.getXmlFragment(NOTE_ROOTS.content);
  const toRoot = newDoc.getXmlFragment(NOTE_ROOTS.content);
  return anchors.map((encoded) => {
    const r = resolveAnchors(oldDoc, [encoded])[0];
    if (!r || r.deleted) return encoded;
    const path = pathOf(r.text, fromRoot);
    const target = path ? atPath(toRoot, path) : null;
    return target ? encodeAnchor(target, r.index) : encoded;
  });
}
function restoreNoteVersion(doc, versionState, origin) {
  const version = new Y.Doc();
  Y.applyUpdate(version, versionState);
  const fromRoot = version.getXmlFragment(NOTE_ROOTS.content);
  const toRoot = doc.getXmlFragment(NOTE_ROOTS.content);
  doc.transact(() => {
    if (toRoot.length > 0) toRoot.delete(0, toRoot.length);
    toRoot.insert(0, pmContentToY(yFragmentToPM(fromRoot).content));
    const strokes = strokesMap(doc);
    for (const key of [...strokes.keys()]) strokes.delete(key);
    for (const { id, stroke } of listStrokes(version)) {
      if (stroke.anchorKind === "text") {
        stroke.anchors = rebaseAnchorsByIndex(version, doc, stroke.anchors);
      }
      putStroke(doc, id, stroke);
    }
  }, origin);
}
function updateOrphanedAt(doc, orphanedAt, now) {
  const orphaned = findOrphanedStrokes(doc);
  const set = new Set(orphaned);
  for (const id of [...orphanedAt.keys()]) if (!set.has(id)) orphanedAt.delete(id);
  for (const id of orphaned) if (!orphanedAt.has(id)) orphanedAt.set(id, now);
  return orphaned;
}
function textNodes(doc) {
  const out = [];
  const visit = (parent) => {
    for (const child of parent.toArray()) {
      if (child instanceof Y.XmlText) out.push(child);
      else if (child instanceof Y.XmlElement) visit(child);
    }
  };
  visit(doc.getXmlFragment(NOTE_ROOTS.content));
  return out;
}
function textOffsetOf(doc, position) {
  let offset = 0;
  for (const t of textNodes(doc)) {
    if (t === position.text) return offset + position.index;
    offset += t.length;
  }
  return null;
}
function positionAtTextOffset(doc, offset) {
  if (offset < 0) return null;
  let rest = offset;
  for (const t of textNodes(doc)) {
    if (rest < t.length) return { text: t, index: rest };
    rest -= t.length;
  }
  return null;
}
function captureAnchorOffsets(doc, strokeIds) {
  const only = strokeIds ? new Set(strokeIds) : null;
  const strokes = /* @__PURE__ */ new Map();
  for (const { id, stroke } of listStrokes(doc)) {
    if (stroke.anchorKind !== "text" || only && !only.has(id)) continue;
    strokes.set(
      id,
      stroke.anchors.map((a) => {
        const r = resolveAnchor(doc, a);
        return r && !r.deleted ? textOffsetOf(doc, r) : null;
      })
    );
  }
  return { strokes };
}
function reanchorStrokes(doc, snapshot, options = {}) {
  const map = options.mapOffset ?? ((o) => o);
  const newId = options.newId ?? (() => uuidv7());
  const replaced = [];
  const current = new Map(listStrokes(doc).map((s) => [s.id, s.stroke]));
  doc.transact(() => {
    for (const [id, offsets] of snapshot.strokes) {
      const stroke = current.get(id);
      if (!stroke) continue;
      const lost = stroke.anchors.some((a, i) => {
        const r = resolveAnchor(doc, a);
        return offsets[i] != null && (!r || r.deleted);
      });
      if (!lost) continue;
      const anchors = stroke.anchors.map((a, i) => {
        const before = offsets[i];
        if (before == null) return a;
        const after = map(before);
        const pos = after == null ? null : positionAtTextOffset(doc, after);
        return pos ? encodeAnchor(pos.text, pos.index) : a;
      });
      const to = newId();
      replaceStrokeWithReanchored(doc, id, anchors, { newId: () => to });
      replaced.push({ from: id, to });
    }
  }, options.origin);
  return replaced;
}
function replaceStrokeWithReanchored(doc, strokeId, anchors, options = {}) {
  const stroke = getStroke(doc, strokeId);
  if (!stroke) return null;
  const to = (options.newId ?? (() => uuidv7()))();
  doc.transact(() => {
    deleteStroke(doc, strokeId);
    putStroke(doc, to, { ...stroke, anchors });
  }, options.origin);
  return to;
}

export { DEFAULT_POINT, STROKE_REBASE_ORIGIN, allAnchorsDeleted, anchorTextStroke, attachAnchorRebase, blockStrokePoints, captureAnchorOffsets, deleteStroke, drawsUnderText, encodeAnchor, findOrphanedStrokes, findPurgeableStrokes, getStroke, isTextAnchored, layoutBlockStroke, layoutTextStroke, listStrokes, packPoints, pointCount, pointsAsFloat32, positionAtTextOffset, pressureCurve, purgeStrokes, putStroke, readStroke, reanchorStrokes, rebaseAnchorList, rebaseAnchorsByIndex, rebaseRedoneAnchors, replaceStroke, replaceStrokeWithReanchored, resolveAnchor, resolveAnchors, restoreNoteVersion, setStrokeHidden, strokeWidthAt, strokesForBlock, strokesMap, textOffsetOf, tiltFactor, unpackPoints, updateOrphanedAt, validateStroke };
//# sourceMappingURL=chunk-STRAO2CU.js.map
//# sourceMappingURL=chunk-STRAO2CU.js.map